import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { createCamera, deleteCamera, patchCamera } from '@sophocles/api/odlApp';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { cameraConfigRequiresCommandLease } from '@sophocles/features/entities/site';
import { addToast } from '@sophocles/features/toast';
import useSiteCommandWrite from '@sophocles/hooks/useSiteCommandWrite';
import { applyApiErrorToFormik, parseApiError } from '@sophocles/utils/apiErrors';
import DeleteCameraDialog from '@sophocles/views/sites/components/CameraCards/DeleteCameraDialog';

// Form field names, used to attribute API validation errors to their input;
// anything the backend reports outside this list downgrades to the banner.
// Limited to the two inputs that actually bind formik.errors below — the
// rest (camera_number select, cic, the mirrored/rotated/motion switches)
// render no error/helperText, so attributing to them would drop the message.
const CAMERA_FIELDS = ['description', 'rotation_amount'];

function determineDialogTitle(device, deviceNumber, isVantage, t) {
  let title = t('camera.newCamera', 'New Camera');

  if (isVantage) {
    if (device) {
      title = t('camera.screenshotSettings', 'Screenshot Settings');
    } else {
      title = t('camera.newScreenshot', 'New Screenshot');
    }
  } else {
    if (device) {
      title = t('camera.cameraNumberSettings', 'Camera {{number}} Settings', { number: deviceNumber });
    }
  }

  return title;
}

function CameraDialog({
  siteId,
  cic,
  camera,
  open,
  isVantage,
  isStandAlone,
  canEdit,
  canDelete,
  cicHasRadio = null,
  onSubmit,
  onClose,
  ...props
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { commandLocked, runWrite } = useSiteCommandWrite(siteId);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      cic: cic?.id,
      camera_number: isVantage ? 1 : _.get(camera, 'camera_number', 0),
      description: _.get(camera, 'description', ''),
      motion_alarm_enabled: _.get(camera, 'motion_alarm_enabled', false),
      mirrored_vertically: _.get(camera, 'mirrored_vertically', false),
      mirrored_horizontally: _.get(camera, 'mirrored_horizontally', false),
      is_rotated: _.get(camera, 'is_rotated', false),
      rotation_amount: _.get(camera, 'rotation_amount', 0),
    },
    validationSchema: yup.object({
      cic: yup.number().nullable(),
      camera_number: yup.number().nullable(),
      description: yup.string(),
      motion_alarm_enabled: yup.boolean(),
      mirrored_vertically: yup.boolean(),
      mirrored_horizontally: yup.boolean(),
      is_rotated: yup.boolean(),
      rotation_amount: yup.number().min(0).max(360),
    }),
    onSubmit: values => {
      // eslint-disable-next-line no-use-before-define -- formik <-> handleSubmit form a submit-time cycle
      handleSubmit(values);
    },
  });

  const handleSubmit = async values => {
    const isUpdating = camera != null;
    const camId = _.get(camera, 'id', 0);
    const requiresCommand = cameraConfigRequiresCommandLease({
      operation: isUpdating ? 'patch' : 'create',
      currentCamera: camera,
      payload: values,
      cicHasRadio,
    });

    setIsSubmitting(true);
    setNonFieldMessages([]);

    try {
      const outcome = await runWrite(
        () => (isUpdating ? patchCamera(siteId, camId, values) : createCamera(siteId, values)),
        { requiresCommand },
      );
      if (!outcome.executed) return;
      dispatch(
        addToast({
          title: t('general.success', 'Success'),
          message: isUpdating
            ? t('camera.settingsUpdated', 'Camera settings updated')
            : t('camera.added', 'Camera added'),
          level: 'success',
          dismissible: true,
        }),
      );
      onClose();
    } catch (error) {
      setNonFieldMessages(
        applyApiErrorToFormik(error, {
          setErrors: formik.setErrors,
          knownFields: CAMERA_FIELDS,
          genericMessage: isUpdating
            ? t('camera.updateFailed', 'Failed to update camera {{id}}', { id: camId })
            : t('camera.createFailed', 'Failed to create camera'),
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async camId => {
    const requiresCommand = cameraConfigRequiresCommandLease({ operation: 'delete', cicHasRadio });
    try {
      const outcome = await runWrite(() => deleteCamera(siteId, camId), { requiresCommand });
      if (!outcome.executed) return;
      dispatch(
        addToast({
          title: t('general.success', 'Success'),
          message: t('camera.deleted', 'Camera deleted'),
          level: 'info',
          dismissible: true,
        }),
      );
    } catch (error) {
      const { nonFieldMessages: deleteMessages } = parseApiError(error, {
        knownFields: [],
        genericMessage: t('camera.deleteFailed', 'Failed to delete camera {{id}}', { id: camId }),
      });
      dispatch(
        addToast({
          title: t('general.error', 'Error'),
          message: deleteMessages[0],
          level: 'error',
          dismissible: true,
        }),
      );
    }
  };

  const handleClose = () => {
    formik.resetForm();
    setNonFieldMessages([]);
    onClose();
  };

  const getCameraNumber = () => {
    return isVantage ? 1 : _.get(camera, 'camera_number', 0);
  };

  const getCameraInstallCount = () => {
    return isVantage ? 1 : _.get(cic, 'cameras_installed', 0);
  };

  const getCameraNumbers = cameras => {
    return isVantage ? 1 : cameras.map(camera => _.get(camera, 'camera_number', 0));
  };

  const getCameras = () => {
    return isVantage ? [] : _.get(cic, 'cameras', []);
  };

  const getCameraNumberOptions = initialCameraNumber => {
    const cameras = getCameras();
    const cameraNumbers = getCameraNumbers(cameras);
    const camerasInstalled = getCameraInstallCount();

    const options = [];
    for (let i = 1; i <= camerasInstalled; i++) {
      if (initialCameraNumber == i || !cameraNumbers.includes(i)) {
        options.push(i);
      }
    }

    return options;
  };

  const cameraNumber = getCameraNumber();
  const cameraNumberChoices = getCameraNumberOptions(cameraNumber);
  const selectBoxValue = camera ? cameraNumber : cameraNumberChoices.length >= 1 ? cameraNumberChoices[0] : '';
  const saveRequiresCommand = cameraConfigRequiresCommandLease({
    operation: camera == null ? 'create' : 'patch',
    currentCamera: camera,
    payload: formik.values,
    cicHasRadio,
  });
  const deleteRequiresCommand = cameraConfigRequiresCommandLease({ operation: 'delete', cicHasRadio });

  const extraActions =
    !isVantage && camera != null && _.get(camera, 'images', []).length === 0
      ? [
          <Button
            key="delete"
            disabled={!canDelete || isDeleteDialogOpen || (commandLocked && deleteRequiresCommand)}
            color="error"
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            {t('actions.delete', 'Delete')}
          </Button>,
          <DeleteCameraDialog
            key="delete-dialog"
            open={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            cameraId={_.get(camera, 'id', 0)}
            onDeleteCamera={handleDelete}
            canSubmit={!(commandLocked && deleteRequiresCommand)}
          />,
        ]
      : null;

  return (
    <ResponsiveDialog
      title={camera ? determineDialogTitle(camera, cameraNumber, isVantage, t) : t('camera.loading', 'Loading')}
      open={open}
      extraActions={extraActions}
      onClose={handleClose}
      onSubmit={() => formik.submitForm()}
      {...props}
      canSubmit={!isSubmitting && !(commandLocked && saveRequiresCommand)}
    >
      <Stack spacing={2}>
        <FormErrorAlert messages={nonFieldMessages} />
        <FormControl fullWidth size="small">
          <InputLabel id="camera-num-select-label" htmlFor="camera-num-select">
            {isVantage ? t('camera.inputNumber', 'Input Number') : t('camera.cameraNumber', 'Camera Number')}
          </InputLabel>
          <Select
            labelId="camera-num-select-label"
            id="camera-num-select"
            name="camera_number"
            value={formik.values.camera_number}
            label={isVantage ? t('camera.inputNumber', 'Input Number') : t('camera.cameraNumber', 'Camera Number')}
            onChange={e => {
              formik.setFieldValue('camera_number', e.target.value);
            }}
            onBlur={formik.onBlur}
          >
            {cameraNumberChoices.map(choice => (
              <MenuItem key={choice} value={choice}>
                {choice}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          name="description"
          label={t('general.description', 'Description')}
          size="small"
          value={formik.values.description}
          error={formik.errors.description}
          helperText={formik.errors.description ?? null}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        <Stack spacing={1}>
          <Typography variant="h6">{t('camera.options', 'Options')}</Typography>
          <Stack spacing={2}>
            {isStandAlone && (
              <div>
                <FormControlLabel
                  name="motion_alarm_enabled"
                  label={t('camera.motionAlarm', 'Motion Alarm')}
                  control={<Checkbox disabled={!canEdit} checked={formik.values.motion_alarm_enabled} />}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ ml: 0, mb: 0 }}
                />
                <Typography variant="caption" component="div" sx={{ ml: '11px' }}>
                  {t(
                    'camera.motionAlarmDescription',
                    'This tells this camera to take pictures when the motion alarm is triggered.',
                  )}
                </Typography>
              </div>
            )}
            <div>
              <FormControlLabel
                name="mirrored_vertically"
                label={t('camera.mirroredVertically', 'Mirrored Vertically')}
                control={<Checkbox disabled={!canEdit} checked={formik.values.mirrored_vertically} />}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                sx={{ ml: 0, mb: 0 }}
              />
              <Typography variant="caption" component="div" sx={{ ml: '11px' }}>
                {t(
                  'camera.mirroredVerticallyDescription',
                  'This option will flip your images vertically. Use this if your images are upside down.',
                )}
              </Typography>
            </div>
            <div>
              <FormControlLabel
                name="mirrored_horizontally"
                label={t('camera.mirroredHorizontally', 'Mirrored Horizontally')}
                control={<Checkbox disabled={!canEdit} checked={formik.values.mirrored_horizontally} />}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                sx={{ ml: 0, mb: 0 }}
              />
              <Typography variant="caption" component="div" sx={{ ml: '11px' }}>
                {t(
                  'camera.mirroredHorizontallyDescription',
                  'This option will flip your image horizontally. Use this if your images are mirrored horizontally.',
                )}
              </Typography>
            </div>
            <div>
              <FormControlLabel
                name="is_rotated"
                label={t('camera.rotate', 'Rotate')}
                control={<Checkbox disabled={!canEdit} checked={formik.values.is_rotated} />}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                sx={{ ml: 0, mb: 0 }}
              />
              <Typography variant="caption" component="div" sx={{ ml: '11px', mb: 2 }}>
                {t(
                  'camera.rotateDescription',
                  'This option will rotate your image. Use this if your images need to be rotated 90, 180 or 270 degrees.',
                )}
              </Typography>
              {formik.values.is_rotated && (
                <TextField
                  fullWidth
                  name="rotation_amount"
                  label={t('camera.rotationAmount', 'Rotation Amount (degrees)')}
                  size="small"
                  value={formik.values.rotation_amount}
                  error={formik.errors.rotation_amount}
                  helperText={formik.errors.rotation_amount ?? null}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              )}
            </div>
          </Stack>
        </Stack>
      </Stack>
    </ResponsiveDialog>
  );
}

export default CameraDialog;
